import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FiShoppingCart, FiStar, FiShare2, FiZoomIn, FiX, FiMaximize2 } from 'react-icons/fi';
import { optimizedAPI } from '../lib/optimizedApi';
import { addToCart } from '../lib/cart';
import toast from 'react-hot-toast';
import ProductCard from '../components/ProductCard';
import SEO from '../components/SEO';
import { generateProductSchema, generateBreadcrumbSchema } from '../lib/structuredData';
import { useCurrency } from '../context/CurrencyContext';

export default function ProductDetail() {
  const { formatPrice } = useCurrency();
  const { slug, id } = useParams();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);

  // Get all available images for the product
  // Handle both old WooCommerce API format (images array) and new optimized API format (image + gallery)
  const productImages = [];

  if (product?.images && Array.isArray(product.images)) {
    // Old WooCommerce API format
    productImages.push(...product.images);
  } else if (product) {
    // New optimized API format
    if (product.image) {
      productImages.push({ src: product.image, alt: product.name });
    }
    if (product.gallery && Array.isArray(product.gallery)) {
      product.gallery.forEach(galleryImage => {
        if (galleryImage && galleryImage !== product.image) {
          productImages.push({ src: galleryImage, alt: product.name });
        }
      });
    }
  }
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [selectedAttributes, setSelectedAttributes] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [activeTab, setActiveTab] = useState('description');
  const [imageZoom, setImageZoom] = useState(false);
  const [stockStatus, setStockStatus] = useState('instock');


  useEffect(() => {
    if (slug || id) {
      fetchProduct();
    }
  }, [slug, id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProduct = async () => {
    setLoading(true);
    const identifier = slug || id;
    console.log('Fetching product with identifier:', identifier);

    try {
      let productData = null;
      let relatedProductsData = [];

      // Use optimized API - it handles both ID and slug
      const identifier = id || slug;
      if (identifier) {
        console.log('Fetching product using optimized API:', identifier);
        try {
          const response = await optimizedAPI.getProductDetail(identifier, true, 4);
          productData = response.product;
          relatedProductsData = response.related_products || [];
          console.log('Found product:', productData?.name);
        } catch (error) {
          console.log('Product not found:', error);
        }
      }

      if (productData) {
        console.log('Product found:', productData.name, 'ID:', productData.id, 'Slug:', productData.slug);
        console.log('Product images:', { image: productData.image, gallery: productData.gallery, all_images: productData.all_images });
        console.log('Product variations:', productData.variations);
        console.log('Product attributes:', productData.attributes);
        setProduct(productData);
        setStockStatus(productData.stock_status);


        setRelatedProducts(relatedProductsData);
      } else {
        console.error('Product not found with identifier:', identifier);
        console.log('Attempting to fetch all products to debug...');

        // Debug: Let's see what products are available
        try {
          const allProducts = await optimizedAPI.getCollectionsProducts({ per_page: 20 });
          console.log('Available products (first 20):', allProducts.map(p => ({ id: p.id, name: p.name, slug: p.slug })));

          // Check if any product has a similar slug
          if (slug) {
            const similarSlugs = allProducts.filter(p =>
              p.slug && p.slug.toLowerCase().includes(slug.toLowerCase())
            );
            if (similarSlugs.length > 0) {
              console.log('Products with similar slugs:', similarSlugs.map(p => ({ id: p.id, name: p.name, slug: p.slug })));
            }
          }
        } catch (debugError) {
          console.error('Debug fetch failed:', debugError);
        }

        toast.error('Product not found');
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Failed to load product');
    } finally {
      setLoading(false);
    }
  };

  // Handle attribute selection for variations
  const handleAttributeChange = async (attributeName, value) => {
    const newAttributes = { ...selectedAttributes, [attributeName]: value };
    setSelectedAttributes(newAttributes);

    // Find matching variation using enhanced variations data
    if (product && product.type === 'variable' && product.variations) {
      const matchingVariation = product.variations.find(variation => {
        return Object.keys(newAttributes).every(attr => {
          const attrValue = newAttributes[attr];
          // Try different attribute key formats
          const possibleKeys = [
            `attribute_${attr}`,
            `attribute_pa_${attr}`,
            `pa_${attr}`,
            attr,
            attr.replace('-', '_')
          ];

          return possibleKeys.some(key => {
            if (variation.attributes && variation.attributes[key]) {
              const variationValue = variation.attributes[key];
              // Check for exact match first
              if (variationValue === attrValue) return true;
              // Check for case-insensitive match
              if (variationValue.toLowerCase() === attrValue.toLowerCase()) return true;
            }
            return false;
          });
        });
      });

      if (matchingVariation) {
        setSelectedVariation(matchingVariation);
        setStockStatus(matchingVariation.stock_status);
        console.log('Selected variation:', matchingVariation);
      } else {
        console.log('No matching variation found for attributes:', newAttributes);
        console.log('Available variations:', product.variations.map(v => ({ id: v.id, attributes: v.attributes })));
      }
    }
  };

  // Get available options for an attribute
  const getAttributeOptions = (attributeName) => {
    if (!product || !product.attributes) return [];

    // Try to find attribute by name (with different variations)
    const attribute = product.attributes.find(attr => {
      const attrName = attr.name.toLowerCase().replace(/\s+/g, '-');
      const attrSlug = attr.name.toLowerCase().replace(/\s+/g, '_');
      const attrPa = `pa_${attr.name.toLowerCase().replace(/\s+/g, '-')}`;

      return attrName === attributeName || attrSlug === attributeName || attrPa === attributeName;
    });

    if (attribute && attribute.options) {
      return attribute.options;
    }

    // If no attribute found, try to get options from variations
    if (product.variations && product.variations.length > 0) {
      const options = new Set();
      product.variations.forEach(variation => {
        if (variation.attributes) {
          Object.entries(variation.attributes).forEach(([key, value]) => {
            if (key.includes(attributeName) || key.includes(attributeName.replace('-', '_'))) {
              if (value && value !== '') {
                options.add(value);
              }
            }
          });
        }
      });
      return Array.from(options);
    }

    return [];
  };

  // Check if all required attributes are selected
  const areAllRequiredAttributesSelected = useMemo(() => {
    if (!product || product.type !== 'variable' || !product.attributes) {
      return true; // Simple products don't need attribute selection
    }

    const requiredAttributes = product.attributes.filter(attr => attr.variation);
    return requiredAttributes.every(attr => {
      const attrName = attr.name.toLowerCase().replace(/\s+/g, '-');
      return selectedAttributes[attrName];
    });
  }, [product, selectedAttributes]);

  // Get current price (variation or product price)
  const currentPrice = useMemo(() => {
    if (selectedVariation) {
      return selectedVariation.sale_price || selectedVariation.regular_price || selectedVariation.price;
    }
    return product?.sale_price || product?.price;
  }, [selectedVariation, product]);

  // Get current regular price
  const currentRegularPrice = useMemo(() => {
    if (selectedVariation) {
      return selectedVariation.regular_price;
    }
    return product?.regular_price;
  }, [selectedVariation, product]);

  // Check if product is on sale
  const isOnSale = useMemo(() => {
    if (selectedVariation) {
      return selectedVariation.sale_price && selectedVariation.sale_price < selectedVariation.regular_price;
    }
    return product?.sale_price && product.sale_price < product.regular_price;
  }, [selectedVariation, product]);



  const handleAddToCart = async () => {
    if (!product) return;

    // Check if all required attributes are selected for variable products
    if (product.type === 'variable' && product.attributes) {
      const requiredAttributes = product.attributes.filter(attr => attr.variation);
      const missingAttributes = requiredAttributes.filter(attr => {
        const attrName = attr.name.toLowerCase().replace(/\s+/g, '-');
        return !selectedAttributes[attrName];
      });

      if (missingAttributes.length > 0) {
        const missingLabels = missingAttributes.map(attr => attr.label || attr.name);
        toast.error(`Please select ${missingLabels.join(', ')}`);
        return;
      }

      // Try to find matching variation if not already selected
      let variationToUse = selectedVariation;
      if (!variationToUse && product.variations) {
        variationToUse = product.variations.find(variation => {
          return Object.keys(selectedAttributes).every(attr => {
            const attrValue = selectedAttributes[attr];
            const possibleKeys = [
              `attribute_${attr}`,
              `attribute_pa_${attr}`,
              `pa_${attr}`,
              attr,
              attr.replace('-', '_')
            ];

            return possibleKeys.some(key => {
              if (variation.attributes && variation.attributes[key]) {
                const variationValue = variation.attributes[key];
                // Check for exact match first
                if (variationValue === attrValue) return true;
                // Check for case-insensitive match
                if (variationValue.toLowerCase() === attrValue.toLowerCase()) return true;
              }
              return false;
            });
          });
        });
      }

      if (!variationToUse) {
        console.log('Selected attributes:', selectedAttributes);
        console.log('Available variations:', product.variations);
        toast.error('No matching variation found. Please try selecting different options.');
        return;
      }

      setIsAddingToCart(true);
      try {
        addToCart(product, quantity, variationToUse);
        toast.success(`${product.name} added to cart`);
        window.dispatchEvent(new Event('cartUpdated'));
      } catch (error) {
        toast.error('Failed to add to cart');
      } finally {
        setIsAddingToCart(false);
      }
    } else {
      // Simple product
      setIsAddingToCart(true);
      try {
        addToCart(product, quantity);
        toast.success(`${product.name} added to cart`);
        window.dispatchEvent(new Event('cartUpdated'));
      } catch (error) {
        toast.error('Failed to add to cart');
      } finally {
        setIsAddingToCart(false);
      }
    }
  };


  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: product.name,
          text: product.short_description,
          url: window.location.href,
        });
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div className="bg-gray-900 h-[60vh] rounded-xl animate-pulse"></div>
              <div className="grid grid-cols-4 gap-2">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="bg-gray-900 h-16 rounded-lg animate-pulse"></div>
                ))}
              </div>
            </div>
            <div className="space-y-4">
              <div className="bg-gray-900 h-6 rounded animate-pulse"></div>
              <div className="bg-gray-900 h-5 rounded w-3/4 animate-pulse"></div>
              <div className="bg-gray-900 h-8 rounded w-1/2 animate-pulse"></div>
              <div className="bg-gray-900 h-24 rounded animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Product Not Found</h1>
          <Link
            to="/collections"
            className="px-6 py-3 bg-pink-500 text-black font-semibold rounded-lg hover:bg-pink-600 transition-colors"
          >
            Back to Products
          </Link>
        </div>
      </div>
    );
  }

  const discountPercentage = isOnSale
    ? Math.round(((currentRegularPrice - currentPrice) / currentRegularPrice) * 100)
    : 0;

  // Generate SEO data
  const seoTitle = product ? `${product.name} | BaileBelle - Premium Ballerina Dresses & Jewelry` : "Product | BaileBelle";
  const seoDescription = product ?
    `${product.short_description || product.description || `Premium ${product.name} from BaileBelle. High-quality ballerina dress with perfect fit and timeless elegance.`}`.substring(0, 160) :
    "Discover premium ballerina dresses and jewelry at BaileBelle. Perfect fit, timeless elegance, and premium quality for every dancer.";
  const seoKeywords = product ?
    `${product.name}, ballerina dress, ballet costume, dance wear, ${product.categories?.map(cat => cat.name).join(', ') || 'dance accessories'}, premium quality, BaileBelle` :
    "ballerina dresses, ballet costumes, dance wear, premium quality";
  const productImage = product?.images?.[0]?.src || "/logo512.png";
  const productUrl = product ? `/products/${product.slug || product.id}` : "";

  // Generate structured data
  const productSchema = product ? generateProductSchema(product) : null;
  const breadcrumbSchema = product ? generateBreadcrumbSchema([
    { name: "Home", url: "https://bailebelle.com" },
    { name: "Products", url: "https://bailebelle.com/products" },
    { name: product.name, url: `https://bailebelle.com/products/${product.slug || product.id}` }
  ]) : null;

  return (
    <>
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        image={productImage}
        url={productUrl}
        type="product"
        structuredData={[productSchema, breadcrumbSchema].filter(Boolean)}
        canonical={productUrl}
      />
      <div className="min-h-screen bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            {/* Product Images */}
            <div className="space-y-3 sm:space-y-4">
              <div className="relative group">
                <div
                  className="relative w-full h-[50vh] sm:h-[60vh] lg:h-[70vh] rounded-lg sm:rounded-xl overflow-hidden cursor-zoom-in"
                  onClick={() => setImageZoom(true)}
                >
                  <img
                    src={productImages[selectedImage]?.src || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5YTNhZiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg=='}
                    alt={productImages[selectedImage]?.alt || product?.name || 'Product Image'}
                    className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
                    loading="eager"
                    decoding="async"
                    onLoad={(e) => {
                      e.target.style.opacity = '1';
                    }}
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5YTNhZiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg==';
                    }}
                    style={{ opacity: 0, transition: 'opacity 0.3s ease-in-out' }}
                  />

                  {/* Zoom Icon */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
                    <FiZoomIn className="text-white text-2xl sm:text-4xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                </div>

                {/* Sale Badge */}
                {isOnSale && (
                  <div className="absolute top-2 left-2 sm:top-3 sm:left-3 bg-gradient-to-r from-red-500 to-red-600 text-white px-2 py-1 rounded-lg text-xs font-bold shadow-lg">
                    -{discountPercentage}% OFF
                  </div>
                )}

                {/* Stock Status */}
                {stockStatus === 'outofstock' && (
                  <div className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-gray-800/90 backdrop-blur-sm text-white px-2 py-1 rounded-lg text-xs font-semibold">
                    Out of Stock
                  </div>
                )}
              </div>

              {/* Thumbnail Images */}
              {productImages && productImages.length > 1 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {productImages.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`relative h-16 sm:h-20 lg:h-24 rounded-lg overflow-hidden border-2 transition-all duration-300 ${selectedImage === index
                        ? 'border-pink-500 scale-105 shadow-lg shadow-pink-500/30'
                        : 'border-gray-700 hover:border-gray-500'
                        }`}
                    >
                      <img
                        src={image.src}
                        alt={image.alt || product?.name || 'Product Image'}
                        className="w-full h-full object-contain"
                        loading="lazy"
                        decoding="async"
                        onLoad={(e) => {
                          e.target.style.opacity = '1';
                        }}
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5YTNhZiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg==';
                        }}
                        style={{ opacity: 0, transition: 'opacity 0.3s ease-in-out' }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="space-y-4 sm:space-y-5">
              <div>
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-2 sm:mb-3 leading-tight">
                  {product.name}
                </h1>
                <div className="flex items-center space-x-2 sm:space-x-3 mb-3 sm:mb-4">
                  <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <FiStar
                        key={i}
                        className={`w-3 h-3 sm:w-4 sm:h-4 ${i < Math.floor(Math.random() * 3) + 3
                          ? 'text-pink-500 fill-current'
                          : 'text-gray-600'
                          }`}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Price */}
              <div className="flex items-center space-x-3 sm:space-x-4">
                <span className="text-xl sm:text-2xl lg:text-3xl font-bold text-pink-500">
                  {formatPrice(currentPrice)}
                </span>
                {isOnSale && (
                  <span className="text-sm sm:text-base lg:text-lg text-gray-400 line-through">
                    {formatPrice(currentRegularPrice)}
                  </span>
                )}
              </div>

              {/* Description */}
              {product.short_description && (
                <div className="text-gray-300 leading-relaxed text-sm sm:text-base">
                  <div dangerouslySetInnerHTML={{ __html: product.short_description }} />
                </div>
              )}

              {/* Product Variations */}
              {product.type === 'variable' && product.attributes && (
                <div className="space-y-3 sm:space-y-4">
                  {product.attributes
                    .filter(attr => attr.variation)
                    .map((attribute) => {
                      const attributeName = attribute.name.toLowerCase().replace(/\s+/g, '-');
                      const options = getAttributeOptions(attributeName);

                      return (
                        <div key={attribute.id} className="space-y-2">
                          <label className="text-white font-semibold text-sm sm:text-base">
                            {attribute.label || attribute.name}:
                          </label>
                          <div className="flex flex-wrap gap-2">
                            {options.map((option) => (
                              <button
                                key={option}
                                onClick={() => handleAttributeChange(attributeName, option)}
                                className={`px-3 py-1.5 rounded-lg border-2 transition-all duration-300 font-medium text-xs sm:text-sm ${selectedAttributes[attributeName] === option
                                  ? 'border-pink-500 bg-pink-500 text-black'
                                  : 'border-gray-600 text-gray-300 hover:border-gray-400 hover:text-white'
                                  }`}
                              >
                                {option}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}

              {/* Quantity and Add to Cart */}
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-center space-x-3 sm:space-x-4">
                  <label className="text-white font-semibold text-sm sm:text-base">Quantity:</label>
                  <div className="flex items-center border-2 border-gray-600 rounded-lg">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="px-2 py-1.5 sm:px-3 sm:py-2 text-white hover:bg-gray-800 transition-colors rounded-l-lg"
                    >
                      -
                    </button>
                    <span className="px-3 py-1.5 sm:px-4 sm:py-2 text-white border-x-2 border-gray-600 text-sm sm:text-base font-semibold">
                      {quantity}
                    </span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="px-2 py-1.5 sm:px-3 sm:py-2 text-white hover:bg-gray-800 transition-colors rounded-r-lg"
                    >
                      +
                    </button>
                  </div>
                </div>

                <div className="flex flex-col gap-2 sm:gap-3">
                  <button
                    onClick={handleAddToCart}
                    disabled={isAddingToCart || stockStatus === 'outofstock' || !areAllRequiredAttributesSelected}
                    className="w-full px-4 py-2.5 sm:px-6 sm:py-3 bg-pink-500 text-black font-bold text-sm sm:text-base rounded-lg hover:bg-pink-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-lg hover:shadow-pink-500/30"
                  >
                    {isAddingToCart ? (
                      <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-black"></div>
                    ) : stockStatus === 'outofstock' ? (
                      'Out of Stock'
                    ) : !areAllRequiredAttributesSelected ? (
                      'Select Options'
                    ) : (
                      <>
                        <FiShoppingCart className="mr-2" size={16} />
                        Add to Cart
                      </>
                    )}
                  </button>


                  <div className="flex gap-2 sm:gap-3">
                    <button
                      onClick={handleShare}
                      className="p-2 sm:p-2.5 text-gray-400 bg-gray-800 rounded-lg hover:text-white hover:bg-gray-700 transition-all duration-300"
                    >
                      <FiShare2 size={18} />
                    </button>

                    <Link
                      to="/size-guide"
                      className="p-2 sm:p-2.5 text-gray-400 bg-gray-800 rounded-lg hover:text-pink-400 hover:bg-gray-700 transition-all duration-300"
                      title="Size Guide"
                    >
                      <FiMaximize2 size={18} />
                    </Link>
                  </div>
                </div>

              </div>

            </div>
          </div>

          {/* Product Details Tabs */}
          <div className="mt-8 sm:mt-10">
            <div className="border-b border-gray-800">
              <nav className="flex space-x-3 sm:space-x-6 overflow-x-auto">
                {[
                  { id: 'description', label: 'Description' }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-2 sm:py-3 px-1 border-b-2 font-semibold text-xs sm:text-sm transition-colors whitespace-nowrap ${activeTab === tab.id
                      ? 'border-pink-500 text-pink-500'
                      : 'border-transparent text-gray-400 hover:text-white'
                      }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            <div className="py-5 sm:py-6">
              {activeTab === 'description' && product.description && (
                <div className="text-gray-300 leading-relaxed text-sm sm:text-base max-w-4xl">
                  <div dangerouslySetInnerHTML={{ __html: product.description }} />
                </div>
              )}

            </div>
          </div>

          {/* Related Products */}
          {relatedProducts.length > 0 && (
            <div className="mt-8 sm:mt-10">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-6 sm:mb-8 text-center">You Might Also Like</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                {relatedProducts.map((relatedProduct) => (
                  <ProductCard key={relatedProduct.id} product={relatedProduct} />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Image Zoom Modal */}
        {imageZoom && (
          <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-2 sm:p-4">
            <div className="relative max-w-6xl max-h-full">
              <button
                onClick={() => setImageZoom(false)}
                className="absolute top-2 right-2 sm:top-4 sm:right-4 z-10 p-2 sm:p-3 bg-gray-800 text-white rounded-full hover:bg-gray-700 transition-colors"
              >
                <FiX size={20} />
              </button>
              <img
                src={productImages[selectedImage]?.src || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5YTNhZiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg=='}
                alt={productImages[selectedImage]?.alt || product?.name || 'Product Image'}
                className="max-w-full max-h-full object-contain rounded-lg"
                loading="eager"
                decoding="async"
                onLoad={(e) => {
                  e.target.style.opacity = '1';
                }}
                onError={(e) => {
                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5YTNhZiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg==';
                }}
                style={{ opacity: 0, transition: 'opacity 0.3s ease-in-out' }}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
}
